// src/views/AccommodationProvider/ProviderAddPropertyAddress.js
import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardTitle, Badge, Row, Col, Alert } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/Axios';

const ProviderAddPropertyAddress = () => {
  const navigate = useNavigate();
  
  // Get provider information from localStorage (set during login)
  const providerType = localStorage.getItem('provider_type');
  const providerName = localStorage.getItem('provider_name');
  const representativeName = localStorage.getItem('representative_name');
  const regStatus = localStorage.getItem('reg_status');

  // Form state
  const [formData, setFormData] = useState({
    trading_name: '',
    address_line_1: '',
    address_line_2: '',
    suburb: '',
    city: '',
    province: '',
    postcode: '',
    country: 'South Africa'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '', visible: false });
  const [validationErrors, setValidationErrors] = useState({});

  // South African provinces
  const southAfricanProvinces = [
    'Eastern Cape',
    'Free State',
    'Gauteng',
    'KwaZulu-Natal',
    'Limpopo',
    'Mpumalanga',
    'Northern Cape',
    'North West',
    'Western Cape'
  ];

  // Load stored address details on component mount
  useEffect(() => {
    const loadStoredAddressDetails = async () => {
      try {
        const response = await axiosInstance.get('/api/property/address/store/');
        if (response.data.stored && response.data.data) {
          const storedData = response.data.data;
          setFormData({
            trading_name: storedData.trading_name || '',
            address_line_1: storedData.address_line_1 || '',
            address_line_2: storedData.address_line_2 || '',
            suburb: storedData.suburb || '',
            city: storedData.city || '',
            province: storedData.province || '',
            postcode: storedData.postcode || '',
            country: storedData.country || 'South Africa'
          });
        }
      } catch (error) {
        console.log('No stored address details found or error loading them');
      }
    };

    loadStoredAddressDetails();
  }, []);

  // Determine welcome message based on provider type
  const getWelcomeMessage = () => {
    if (providerType === 'individual') {
      return `Property Address - ${providerName || 'Individual Provider'}`;
    } else if (providerType === 'entity') {
      return `Property Address - ${providerName || 'Your Organization'}`;
    } else {
      return 'Property Address Details';
    }
  };

  const getSubtitleMessage = () => {
    if (providerType === 'entity' && representativeName) {
      return `Representative: ${representativeName}`;
    }
    return null;
  };

  const getProviderTypeDisplay = () => {
    if (providerType === 'individual') {
      return 'Individual Property Owner';
    } else if (providerType === 'entity') {
      return 'Business/Organization';
    }
    return 'Accommodation Provider';
  };

  const getRegistrationStatusMessage = () => {
    switch (regStatus) {
      case '0':
        return {
          message: 'Please complete your registration to add properties',
          variant: 'warning'
        };
      case '1':
        return {
          message: 'Registration under review - Property addition will be available once approved.',
          variant: 'info'
        };
      case '2':
        return {
          message: 'Registration approved - Step 1: Property Address Details',
          variant: 'success'
        };
      default:
        return {
          message: 'Please complete your registration',
          variant: 'secondary'
        };
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBackToPropertyManagement = () => {
    navigate('/provider-property-management');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setAlert({ type: '', message: '', visible: false });
    setValidationErrors({});

    // Basic client-side validation
    const errors = {};
    
    if (!formData.trading_name.trim()) {
      errors.trading_name = 'Trading name is required.';
    } else if (formData.trading_name.trim().length < 3) {
      errors.trading_name = 'Trading name must be at least 3 characters long.';
    }
    
    if (!formData.address_line_1.trim()) {
      errors.address_line_1 = 'Address Line 1 is required.';
    }

    if (!formData.suburb.trim()) {
      errors.suburb = 'Suburb is required.';
    }

    if (!formData.city.trim()) {
      errors.city = 'City is required.';
    }

    if (!formData.province) {
      errors.province = 'Province is required.';
    }

    if (!formData.postcode.trim()) {
      errors.postcode = 'Postal code is required.';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setAlert({
        type: 'danger',
        message: 'Please fill in all required fields.',
        visible: true
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const requestData = {
        addresslines: formData.address_line_1.trim() + (formData.address_line_2.trim() ? '\n' + formData.address_line_2.trim() : ''),
        suburb: formData.suburb.trim(),
        city: formData.city.trim(),
        province: formData.province,
        postcode: formData.postcode.trim(),
        country: formData.country,
        trading_name: formData.trading_name.trim()
      };
      
      console.log('Sending address request data:', requestData);
      
      const response = await axiosInstance.post(
        `/api/property/address/store/`,
        requestData
      );

      if (response.status === 200) {
        setAlert({
          type: 'success',
          message: response.data.message || 'Property address saved successfully! Proceed to next step.',
          visible: true
        });
        
        // Navigate to contact step after a delay
        setTimeout(() => {
          navigate('/provider-add-property-room-details');
        }, 2000);
      }
    } catch (error) {
      console.error('Error saving property address:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.data?.validation_errors) {
        const backendValidationErrors = error.response.data.validation_errors;
        
        // Convert backend validation errors to frontend format
        const frontendErrors = {};
        Object.entries(backendValidationErrors).forEach(([field, messages]) => {
          frontendErrors[field] = Array.isArray(messages) ? messages[0] : messages;
        });
        
        setValidationErrors(frontendErrors);
        setAlert({
          type: 'danger',
          message: 'Please correct the errors.',
          visible: true
        });
      } else if (error.response?.data?.error) {
        setAlert({
          type: 'danger',
          message: error.response.data.error,
          visible: true
        });
      } else {
        setAlert({
          type: 'danger',
          message: `Failed to save property address. Status: ${error.response?.status}. Please try again.`,
          visible: true
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">{getWelcomeMessage()}</h2>
              {getSubtitleMessage() && (
                <p className="text-muted mb-1">{getSubtitleMessage()}</p>
              )}
              <Badge color="info" className="me-2">
                {getProviderTypeDisplay()}
              </Badge>
              <Badge color="secondary" className="me-2">
                Step 1 of 5
              </Badge>
            </div>
            <div>
              <button 
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleBackToPropertyManagement}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Back to Property Management
              </button>
            </div>
          </div>
          
          {/* Registration Status Alert */}
          <div className={`alert alert-${getRegistrationStatusMessage().variant} alert-dismissible fade show`} role="alert">
            <i className="bi bi-info-circle me-2"></i>
            {getRegistrationStatusMessage().message}
          </div>
        </div>
      </div>

      {/* Form Alert */}
      {alert.visible && (
        <Row className="mb-4">
          <Col md="12">
            <Alert color={alert.type} toggle={() => setAlert({...alert, visible: false})}>
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Property Address Form */}
      <Row className="mb-4">
        <Col md="10" className="mx-auto">
          <Card>
            <CardBody>
              <CardTitle tag="h4" className="mb-4">
                <i className="bi bi-geo-alt me-2 text-primary"></i>
                Property Address Information
              </CardTitle>
              
              <form onSubmit={handleSubmit}>
                <h3>Property Address Details</h3>

                {/* Trading Name - Full width */}
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.trading_name ? 'is-invalid' : ''}`}
                    type="text"
                    id="tradingNameInput"
                    name="trading_name"
                    placeholder="Trading Name"
                    value={formData.trading_name}
                    onChange={handleInputChange}
                    disabled={regStatus !== '2' || isSubmitting}
                  />
                  <label htmlFor="tradingNameInput">Trading Name (e.g., Micasa Student Housing, Sunset Beds)</label>
                  {validationErrors.trading_name && (
                    <div className="invalid-feedback">{validationErrors.trading_name}</div>
                  )}
                </div>

                {/* Country - Full width - Locked to South Africa */}
                <div className="form-floating mb-3">
                  <input
                    className="form-control"
                    type="text"
                    id="countryInput"
                    value="South Africa"
                    readOnly
                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                  />
                  <label htmlFor="countryInput">Country</label>
                </div>

                {/* Province/State - Full width - South African Provinces */}
                <div className="form-floating mb-3">
                  <select
                    className={`form-select ${validationErrors.province ? 'is-invalid' : ''}`}
                    id="provinceInput"
                    value={formData.province}
                    onChange={(e) => handleInputChange(e)}
                    name="province"
                    disabled={regStatus !== '2' || isSubmitting}
                  >
                    <option value="">Select Province</option>
                    {southAfricanProvinces.map((province) => (
                      <option key={province} value={province}>
                        {province}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="provinceInput">Province</label>
                  {validationErrors.province && (
                    <div className="invalid-feedback">{validationErrors.province}</div>
                  )}
                </div>

                {/* Address Line 1 and Address Line 2 - Horizontally aligned */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        className={`form-control ${validationErrors.address_line_1 ? 'is-invalid' : ''}`}
                        type="text"
                        id="addressLine1Input"
                        name="address_line_1"
                        placeholder="Address Line 1"
                        value={formData.address_line_1}
                        onChange={handleInputChange}
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <label htmlFor="addressLine1Input">Address Line 1</label>
                      {validationErrors.address_line_1 && (
                        <div className="invalid-feedback">{validationErrors.address_line_1}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        className={`form-control ${validationErrors.address_line_2 ? 'is-invalid' : ''}`}
                        type="text"
                        id="addressLine2Input"
                        name="address_line_2"
                        placeholder="Address Line 2 (Optional)"
                        value={formData.address_line_2}
                        onChange={handleInputChange}
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <label htmlFor="addressLine2Input">Address Line 2 (Optional)</label>
                      {validationErrors.address_line_2 && (
                        <div className="invalid-feedback">{validationErrors.address_line_2}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Suburb and City - Horizontally aligned */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        className={`form-control ${validationErrors.suburb ? 'is-invalid' : ''}`}
                        type="text"
                        id="suburbInput"
                        name="suburb"
                        placeholder="Suburb"
                        value={formData.suburb}
                        onChange={handleInputChange}
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <label htmlFor="suburbInput">Suburb</label>
                      {validationErrors.suburb && (
                        <div className="invalid-feedback">{validationErrors.suburb}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        className={`form-control ${validationErrors.city ? 'is-invalid' : ''}`}
                        type="text"
                        id="cityInput"
                        name="city"
                        placeholder="City"
                        value={formData.city}
                        onChange={handleInputChange}
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <label htmlFor="cityInput">City</label>
                      {validationErrors.city && (
                        <div className="invalid-feedback">{validationErrors.city}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Postcode - Full width */}
                <div className="form-floating mb-3">
                  <input
                    className={`form-control ${validationErrors.postcode ? 'is-invalid' : ''}`}
                    type="text"
                    id="postcodeInput"
                    name="postcode"
                    placeholder="Postal code/Zip"
                    value={formData.postcode}
                    onChange={handleInputChange}
                    maxLength="4"
                    pattern="[0-9]{4}"
                    disabled={regStatus !== '2' || isSubmitting}
                  />
                  <label htmlFor="postcodeInput">Postal code/Zip</label>
                  {validationErrors.postcode && (
                    <div className="invalid-feedback">{validationErrors.postcode}</div>
                  )}
                </div>

                {/* Navigation buttons */}
                <div className="row mt-4">
                  <div className="col-md-12 text-end">
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={regStatus !== '2' || isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          Next
                          <i className="bi bi-arrow-right ms-2"></i>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {alert.type === 'danger' && (
                  <div className="alert alert-danger mt-3">
                    {alert.message}
                  </div>
                )}
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Help Section */}
      <Row>
        <Col md="12">
          <Card className="border-0 bg-light">
            <CardBody>
              <CardTitle tag="h6" className="mb-3">
                <i className="bi bi-info-circle me-2"></i>
                Address Guidelines
              </CardTitle>
              <Row>
                <Col md="4">
                  <div className="mb-2">
                    <strong>Address Accuracy:</strong>
                  </div>
                  <ul className="small text-muted">
                    <li>Provide the exact street address</li>
                    <li>Include unit numbers if applicable</li>
                    <li>Ensure postal code is correct</li>
                    <li>Students will use this for navigation</li>
                  </ul>
                </Col>
                <Col md="4">
                  <div className="mb-2">
                    <strong>Location Requirements:</strong>
                  </div>
                  <ul className="small text-muted">
                    <li>Property must be within South Africa</li>
                    <li>Should be accessible by public transport</li>
                    <li>Near educational institutions preferred</li>
                    <li>Safe neighborhood is essential</li>
                  </ul>
                </Col>
                <Col md="4">
                  <div className="mb-2">
                    <strong>Next Steps:</strong>
                  </div>
                  <div className="small text-muted">
                    After saving the address details, you'll proceed to upload 
                    required property documents and photos, then schedule an 
                    inspection before your property can go live.
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style jsx>{`
        /* h3 styling to match Step 1 */
        h3 {
          font-size: 22px;
          color: #495057;
          font-weight: 600;
          margin: 2rem 0 1.5rem 0;
          padding: 0.75rem 0;
          border-bottom: 2px solid #e9ecef;
          position: relative;
        }
        
        h3::before {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 60px;
          height: 2px;
          background: linear-gradient(45deg, #0d6efd, #6610f2);
          border-radius: 1px;
        }
        
        h3:first-child {
          margin-top: 0;
        }

        /* MDB5 validation styles - matching Step2Address */
        .form-control.is-invalid,
        .form-select.is-invalid {
          border-color: #dc3545;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          background-color: rgba(220, 53, 69, 0.05);
        }
        
        .form-control.is-invalid:focus,
        .form-select.is-invalid:focus {
          border-color: #dc3545;
          box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
          background-color: rgba(220, 53, 69, 0.05);
        }
        
        .invalid-feedback {
          display: block;
          width: 100%;
          margin-top: 0.5rem;
          font-size: 0.875rem;
          color: #fff;
          text-align: left;
          position: relative;
          z-index: 1;
          padding: 0.5rem 0.75rem;
          background: #dc3545;
          border-radius: 0.375rem;
          border: 1px solid #dc3545;
        }
        
        /* For form containers, ensure validation message doesn't overlap */
        .form-outline .invalid-feedback,
        .form-floating .invalid-feedback {
          margin-top: 0.5rem;
          position: relative;
          clear: both;
        }
        
        /* Add arrow pointer to validation message */
        .invalid-feedback::before {
          content: '';
          position: absolute;
          top: -5px;
          left: 1rem;
          width: 0;
          height: 0;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-bottom: 5px solid #dc3545;
        }

        /* Form floating enhancements */
        .form-floating > .form-control:focus,
        .form-floating > .form-select:focus {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
        }

        .form-floating > label {
          color: #6c757d;
        }

        .form-floating > .form-control:focus ~ label,
        .form-floating > .form-control:not(:placeholder-shown) ~ label,
        .form-floating > .form-select ~ label {
          opacity: 0.65;
          transform: scale(0.85) translateY(-0.5rem) translateX(0.15rem);
        }


      `}</style>
    </div>
  );
};

export default ProviderAddPropertyAddress;